import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loginUser, checkOtp, getInvitation } from "./auth.actions";
import type {
  FetchLoginResponse,
  FetchCheckOtpResponse,
  Auth,
  FetchGetInvitationResponse,
} from "features/auth/auth.types";

const initialState: Auth = {
  data: {
    name: "",
    email: "",
    phone: "",
    country_code: "",
    login: "",
    photo: "",
    client_id: 0,
    email_otp_enabled: null,
    sms_otp_enabled: null,
    is_prioritized_user_preference_enabled: null,
    token: "",
    first_time_login: 0,
    manage_user: null,
    is_sso_login: false,
    id_token: null,
    clients: [],
  },
  invitation: {
    invitation_key: "",
    email: "",
    client: {
      id: 0,
      name: "",
    },
  },
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setAuth: (state, action: PayloadAction<Partial<typeof initialState.data>>) => {
      state.data = { ...state.data, ...action.payload };
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      loginUser.fulfilled,
      (state, action: PayloadAction<FetchLoginResponse>) => {
        state.data = { ...state.data, ...action.payload };
        state.isLoading = false;
        state.error = null;
      }
    );
    builder.addCase(loginUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });
    builder.addCase(checkOtp.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      checkOtp.fulfilled,
      (state, action: PayloadAction<FetchCheckOtpResponse>) => {
        state.data = { ...state.data, ...action.payload };
        state.isLoading = false;
        state.error = null;
      }
    );
    builder.addCase(checkOtp.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });
    builder.addCase(getInvitation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getInvitation.fulfilled,
      (state, action: PayloadAction<FetchGetInvitationResponse>) => {
        // Only populate state.invitation when the backend returned full invitation data.
        // For "register"/"login" flows, the page handles the redirect directly via unwrap().
        if (action.payload.flow === "ok") {
          state.invitation = {
            invitation_key: action.payload.invitation_key,
            email: action.payload.email,
            client: action.payload.client,
          };
        }
        state.isLoading = false;
        state.error = null;
      }
    );
    builder.addCase(getInvitation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });
  },
});

export const { resetState, setAuth } = authSlice.actions;
export const authReducer = authSlice.reducer;
